import React from 'react';
import {Link, Typography} from '@genestack/ui';
import {PublicationSource, PubMedSource, PubMedIdSource, BookSource, UrlSource} from './interface';
import {pubMedBaseUrl} from '../../../../variables';
import {metadataArrayToString} from './helpers';

interface Props {
    source: PublicationSource;
}

function isPubMed(source: PublicationSource): source is PubMedSource {
    return source.type === 'PUB_MED';
}

function isPubMedId(source: PublicationSource): source is PubMedIdSource {
    return source.type === 'PUB_MED_ID';
}

function isBook(source: PublicationSource): source is BookSource {
    return source.type === 'BOOK';
}

function isUrlSource(source: PublicationSource): source is UrlSource {
    return source.type === 'URL';
}

function getAuthorsString(authors: string[]): string {
    if (!authors.length) {
        return '';
    }

    return `${metadataArrayToString(authors)}. `;
}

function getStringWithDot(input: string | null): string {
    if (!input) {
        return '';
    }

    return `${input}${input.endsWith('.') ? '' : '.'} `;
}

function getStringWithComa(input: string | null): string {
    if (!input) {
        return '';
    }

    return `${input}${input.endsWith(',') ? '' : ','} `;
}

const InvalidSource = () => <Typography box="paragraph">Invalid source</Typography>;

export function DocumentSource({source}: Props) {
    return (
        <li>
            {(function () {
                if (isPubMedId(source)) {
                    if (!source.pubMedId) {
                        return <InvalidSource />;
                    }

                    return (
                        <Typography box="paragraph">
                            PubMed ID:{' '}
                            <Link href={`${pubMedBaseUrl}${source.pubMedId}`} target="_blank">
                                {source.pubMedId}
                            </Link>
                        </Typography>
                    );
                }

                if (isPubMed(source)) {
                    if (
                        !source.title &&
                        !source.authors.length &&
                        !source.journal &&
                        !source.publicationYear &&
                        !source.pubMedId
                    ) {
                        return <InvalidSource />;
                    }
                    return (
                        <Typography box="paragraph">
                            {`${getAuthorsString(source.authors)}${getStringWithDot(source.title)}${getStringWithComa(source.journal)}${getStringWithDot(source.publicationYear)}`}
                            {source.pubMedId && (
                                <React.Fragment>
                                    PubMed ID:{' '}
                                    <Link
                                        href={`${pubMedBaseUrl}${source.pubMedId}`}
                                        target="_blank"
                                    >
                                        {source.pubMedId}
                                    </Link>
                                </React.Fragment>
                            )}
                        </Typography>
                    );
                }

                if (isBook(source)) {
                    if (!source.title && !source.authors.length && !source.publicationYear) {
                        return <InvalidSource />;
                    }

                    return (
                        <Typography box="paragraph">
                            {`${getAuthorsString(source.authors)}${getStringWithDot(source.title)}${getStringWithDot(source.publicationYear)}`}
                        </Typography>
                    );
                }

                if (isUrlSource(source)) {
                    if (!source.url) {
                        return <InvalidSource />;
                    }

                    return (
                        <Typography box="paragraph">
                            <Link href={source.url} target="_blank">
                                {source.url}
                            </Link>
                        </Typography>
                    );
                }

                if (
                    !source.title &&
                    !source.authors.length &&
                    !source.publisher &&
                    !source.publicationYear
                ) {
                    return <InvalidSource />;
                }

                return (
                    <Typography box="paragraph">
                        {`${getAuthorsString(source.authors)}${getStringWithDot(source.title)}${getStringWithComa(source.publisher)}${getStringWithDot(source.publicationYear)}`}
                    </Typography>
                );
            })()}
        </li>
    );
}
